/**
 * A single pod thread: its messages plus a reply box.
 *
 * Students, guardians, and tutors all reply through the same box; the server resolves the sender
 * role and rejects anyone who is not a live participant. Deleted messages show a placeholder and
 * never their original content.
 */
import { useState, useRef, useEffect } from 'react'
import { ArrowLeft, Send, Users, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import podMessagingService from '../../services/podMessagingService'

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '')

const ROLE_STYLE = {
  tutor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  student: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  guardian: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
}

function audienceSummary(thread) {
  if (thread.audienceType === 'cohort') return 'To the whole cohort'
  if (thread.audienceType === 'group') return `To group: ${thread.group?.name || 'group'}`
  if (thread.audienceType === 'class') return `To class: ${thread.class?.name || 'class'}`
  const names = (thread.audience || [])
    .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim())
    .filter(Boolean)
  return names.length ? `To: ${names.join(', ')}` : 'To selected students'
}

export default function PodThreadView({ thread, loading, onBack, onReplied }) {
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [thread?.messages?.length])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading conversation
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="hidden h-full items-center justify-center p-8 text-center text-sm text-gray-500 md:flex">
        Select a conversation to read it.
      </div>
    )
  }

  const send = async (e) => {
    e.preventDefault()
    const content = reply.trim()
    if (!content) return
    setSending(true)
    try {
      await podMessagingService.postMessage(thread.id, content)
      setReply('')
      await onReplied()
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || 'Could not send the reply.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex h-full flex-col bg-white dark:bg-gray-900">
      <div className="flex items-start gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to conversations"
          className="rounded p-1 text-gray-500 hover:bg-gray-100 md:hidden dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">
            {thread.subject || 'Pod conversation'}
          </h3>
          <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Users className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {thread.organization?.name ? `${thread.organization.name} · ` : ''}
              {audienceSummary(thread)}
            </span>
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {(thread.messages || []).map((m) => (
          <div key={m.id} className="border-l-2 border-indigo-200 pl-3 dark:border-indigo-800">
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">{m.senderName}</span>
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                  ROLE_STYLE[m.senderRole] || 'bg-gray-100 text-gray-600'
                }`}
              >
                {m.senderRole}
              </span>
              <span>{fmtTime(m.createdAt)}</span>
            </div>
            <p
              className={
                m.isDeleted
                  ? 'text-sm italic text-gray-400'
                  : 'whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200'
              }
            >
              {m.content}
            </p>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={send}
        className="flex items-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700"
      >
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={2}
          maxLength={10000}
          aria-label="Reply"
          placeholder="Write a reply"
          className="flex-1 rounded border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
        <button
          type="submit"
          disabled={sending || !reply.trim()}
          className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {sending ? 'Sending' : 'Reply'}
        </button>
      </form>
    </div>
  )
}
